import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Shield, Loader2, X, RefreshCw, CheckCircle, ScanSearch, Lock, ImageOff, Undo2, Trash2 } from "lucide-react";
import { SwitchToggle } from "@/components/toggles";
import api from "@/lib/api";

const DEFAULT_PRIVACY = {
  pixelate_face: false,
  blur_face: false,
  remove_tattoos: false,
};

/**
 * BrushCanvas — iter282 · Freies Übermalen (Pinsel) beliebiger Formen.
 * Sammelt normalisierte (0..1) Strichpunkte, gruppiert je Zug (für Undo), und
 * zeichnet sie als weiche Kreise zur visuellen Rückmeldung.
 */
export function BrushCanvas({ imageUrl, strokes, onChange, radiusPct, color, eraseMode }) {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hover, setHover] = useState(null);

  const measure = () => {
    const img = imgRef.current; const c = canvasRef.current;
    if (!img || !c) return;
    // Canvas exakt auf die tatsächlich dargestellte Bildgröße (object-contain) legen.
    c.width = img.clientWidth; c.height = img.clientHeight;
    redraw();
  };
  const redraw = () => {
    const c = canvasRef.current; if (!c || !c.width) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = color || "rgba(245,158,11,0.45)";
    // iter173x-fix · Radius PRO STRICH (stroke.r) → bereits gemalte Striche behalten
    // ihre Größe, auch wenn danach eine andere Pinselgröße gewählt wird.
    (strokes || []).forEach((stroke) => {
      const r = ((stroke && stroke.r) || radiusPct || 0.05) * c.width;
      ((stroke && stroke.pts) || []).forEach((p) => {
        ctx.beginPath(); ctx.arc(p.x * c.width, p.y * c.height, r, 0, Math.PI * 2); ctx.fill();
      });
    });
    // Radierer-Cursor blau anzeigen → man sieht, was man wegradiert.
    if (eraseMode && hover) {
      const er = (radiusPct || 0.03) * c.width;
      ctx.beginPath(); ctx.arc(hover.x * c.width, hover.y * c.height, er, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(37,99,235,0.28)"; ctx.fill();
      ctx.strokeStyle = "rgba(37,99,235,0.95)"; ctx.lineWidth = 2; ctx.stroke();
    }
  };
  useEffect(() => { redraw(); }); // redraw on every render (strokes/radius change)
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const norm = (e) => {
    // Koordinaten relativ zur dargestellten Bildfläche normalisieren.
    const rect = imgRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: Math.min(1, Math.max(0, (cx - rect.left) / rect.width)), y: Math.min(1, Math.max(0, (cy - rect.top) / rect.height)) };
  };
  const down = (e) => { e.preventDefault(); drawingRef.current = true; if (eraseMode) { eraseAt(norm(e)); return; } onChange([...(strokes || []), { r: radiusPct || 0.05, pts: [norm(e)] }]); };
  const move = (e) => {
    const p = norm(e);
    if (eraseMode) setHover(p);
    if (!drawingRef.current) return;
    e.preventDefault();
    if (eraseMode) { eraseAt(p); return; }
    const arr = strokes.slice();
    const last = arr[arr.length - 1];
    arr[arr.length - 1] = { ...last, pts: [...last.pts, p] };
    onChange(arr);
  };
  const up = () => { drawingRef.current = false; };
  const leave = () => { drawingRef.current = false; setHover(null); };
  const eraseAt = (p) => {
    const er = (radiusPct || 0.03);
    const next = (strokes || [])
      .map((s) => ({ ...s, pts: (s.pts || []).filter((pt) => Math.hypot(pt.x - p.x, pt.y - p.y) > er) }))
      .filter((s) => (s.pts || []).length > 0);
    onChange(next);
  };

  return (
    <div
      data-testid="brush-canvas"
      className="relative select-none inline-block"
      style={{ touchAction: "none", cursor: "crosshair", lineHeight: 0 }}
      onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={leave}
      onTouchStart={down} onTouchMove={move} onTouchEnd={up}
    >
      <img
        ref={imgRef}
        src={imageUrl} alt="" draggable={false}
        onLoad={() => setTimeout(measure, 30)}
        className="object-contain pointer-events-none block"
        style={{ maxHeight: "40vh", maxWidth: "100%" }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
    </div>
  );
}

/**
 * AnonymizeDialog — generische, wiederverwendbare Anonymisierungs-Engine.
 * Nimmt eine Bild-Blob/File entgegen, führt Upload → (optional) Vorschau/
 * Anonymisierung → Confirm durch und liefert via onComplete(finalUrl) die
 * finale /api/files-URL zurück. Premium-Gating (Quota + Buchung) inklusive.
 */
export function AnonymizeDialog({ file, context = "GENERIC", returnBlob = false, onComplete, onCancel }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [staging, setStaging] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [privacy, setPrivacy] = useState(DEFAULT_PRIVACY);
  const [quota, setQuota] = useState(null);
  const [isAnonymized, setIsAnonymized] = useState(false);
  const [phase, setPhase] = useState("uploading"); // uploading | ready | anonymizing | confirming
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [brushMode, setBrushMode] = useState(false); // Blur-Pinsel (verwischen)
  const [brushStrokes, setBrushStrokes] = useState([]); // Array von Zügen: {r, pts:[{x,y}]}
  const [brushSize, setBrushSize] = useState(0.035);
  const [elapsed, setElapsed] = useState(0);
  const startedRef = useRef(false);
  const abortRef = useRef(false);

  const loadPreview = async (stagingId, variant) => {
    try {
      const res = await api.get(`/anonymize/staging/${stagingId}/preview`, {
        params: { variant, t: Date.now() },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPreviewBlob(res.data);
      setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (startedRef.current || !file) return;
    startedRef.current = true;
    (async () => {
      try {
        const fd = new FormData();
        const name = file.name || "image.jpg";
        fd.append("file", file, name);
        fd.append("context", context);
        const { data } = await api.post("/anonymize/staging", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
          onUploadProgress: (evt) => {
            if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        setStaging(data);
        setQuota(data.quota || null);
        // iter173v · NSFW-Scan läuft asynchron → Status pollen, bis freigegeben/blockiert.
        if (data.scan_required && data.status === "SCANNING") {
          setPhase("scanning");
          const scanUrl = `/anonymize/staging/${data.staging_id}/scan-status`;
          const deadline = Date.now() + 120000;
          let scan = null;
          while (Date.now() < deadline) {
            await new Promise((r) => setTimeout(r, 2000));
            let st;
            try { st = (await api.get(scanUrl, { timeout: 20000 })).data; } catch { continue; }
            if (st.status === "BLOCKED") { scan = st; break; }
            if (st.status === "UPLOADED") { scan = st; break; }
          }
          if (scan?.status === "BLOCKED") {
            setError(scan.message || t("anonymize.err_upload_failed"));
            setPhase("blocked");
            return;
          }
          setStaging((s) => ({ ...(s || {}), ...(scan || {}), status: "UPLOADED" }));
        }
        await loadPreview(data.staging_id, "raw");
        setPhase("ready");
      } catch (e) {
        const d = e.response?.data?.detail;
        setError((typeof d === "string" ? d : d?.message) || t("anonymize.err_upload_failed"));
        setPhase("ready");
      }
    })();
  }, [file, context]);

  const previewBlocked = quota && quota.gated && !quota.unlimited && (quota.allowed === false);

  const createPreview = async () => {
    if (!staging || previewBlocked) return;
    const flatWithR = (sk) => (sk || []).flatMap((s) => (s.pts || []).map((p) => ({ x: p.x, y: p.y, r: s.r })));
    const brushPoints = flatWithR(brushStrokes);
    if (brushMode && brushPoints.length === 0) {
      setError(t("anonymize.err_brush_empty"));
      return;
    }
    setPhase("anonymizing"); setError(""); setWarn(""); setElapsed(0);
    abortRef.current = false;
    const startTs = Date.now();
    const ticker = setInterval(() => setElapsed(Math.floor((Date.now() - startTs) / 1000)), 1000);
    // iter327 · Gesichts-/Tattoo-Erkennung kann bei Bildern ohne Treffer ewig hängen.
    // Nach 60 s abbrechen und klar melden, was nicht gefunden wurde.
    const wantFace = privacy.pixelate_face || privacy.blur_face;
    const wantTattoo = !!privacy.remove_tattoos;
    try {
      const body = { ...privacy };
      if (brushPoints.length) { body.brush_strokes = brushPoints; body.brush_radius = brushSize; }
      // iter173n · Asynchron: Job starten → Status pollen (kein Proxy-Timeout mehr).
      const { data: started } = await api.post(
        `/anonymize/staging/${staging.staging_id}/anonymize`, body, { timeout: 60000 });
      if (started.quota) setQuota(started.quota);
      const statusUrl = `/anonymize/staging/${staging.staging_id}/anonymize-status`;
      const deadline = Date.now() + 60000;
      let result = null;
      while (Date.now() < deadline) {
        if (abortRef.current) return;
        await new Promise((r) => setTimeout(r, 2500));
        if (abortRef.current) return;
        let st;
        try { st = (await api.get(statusUrl, { timeout: 30000 })).data; } catch { continue; }
        if (st.state === "DONE") { result = st; break; }
        if (st.state === "FAILED") { throw new Error(st.error || "failed"); }
      }
      if (abortRef.current) return;
      if (!result) {
        // Kein Ergebnis innerhalb von 60 s → nur bei Gesichts-/Tattoo-Erkennung
        // mit spezifischer "nicht gefunden"-Meldung; sonst generischer Timeout.
        if ((wantFace || wantTattoo) && !brushPoints.length) {
          const parts = [];
          if (wantFace) parts.push(t("anonymize.timeout_face"));
          if (wantTattoo) parts.push(t("anonymize.timeout_tattoo"));
          setError(t("anonymize.timeout_60s", { items: parts.join(t("anonymize.timeout_and")) }));
          return;
        }
        throw new Error("timeout");
      }
      setQuota(result.quota || quota);
      setIsAnonymized(true);
      if ((privacy.pixelate_face || privacy.blur_face) && result.no_face_detected) {
        setWarn(t("anonymize.no_face_brush_warn"));
      }
      if (result.tattoo_error === "RESOURCES_BUSY") {
        setWarn(t("anonymize.tattoo_busy"));
      } else if (result.marks_requested && !result.marks_applied) {
        setWarn(t("anonymize.marks_not_applied"));
      }
      if (brushPoints.length) setBrushMode(false);
      await loadPreview(staging.staging_id, "anonymized");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error_code === "ANON_QUOTA_EXCEEDED") {
        setQuota((q) => ({ ...(q || {}), gated: true, unlimited: false, allowed: false,
          used: d.used, limit: d.limit, plan_code: d.plan_code }));
        setError(d.message || "Kontingent aufgebraucht.");
      } else if (typeof d === "string" || d?.message) {
        setError(d?.message || d);
      } else {
        setError(t("anonymize.err_timeout"));
      }
    } finally {
      clearInterval(ticker);
      setPhase((p) => (p === "anonymizing" ? "ready" : p));
    }
  };

  const applyUpload = async () => {
    if (!staging) return;
    setPhase("confirming"); setError("");
    // returnBlob-Modus: finales Bild NICHT serverseitig speichern, sondern die
    // (ggf. anonymisierte) Blob an den Parent zurückgeben (z.B. Artikelbild-Upload,
    // der den bestehenden /upload-Explicit-Block durchlaufen soll).
    if (returnBlob) {
      try {
        const ext = (previewBlob?.type || "").includes("png") ? "png" : "jpg";
        const outFile = previewBlob
          ? new File([previewBlob], `anon.${ext}`, { type: previewBlob.type || "image/jpeg" })
          : file;
        try { await api.delete(`/anonymize/staging/${staging.staging_id}`); } catch { /* cleanup */ }
        if (preview) URL.revokeObjectURL(preview);
        onComplete?.(outFile, { anonymized: isAnonymized });
      } catch (e) {
        setError(t("anonymize.error_apply"));
        setPhase("ready");
      }
      return;
    }
    try {
      const { data } = await api.post(
        `/anonymize/staging/${staging.staging_id}/confirm`, { use_anonymized: isAnonymized });
      if (preview) URL.revokeObjectURL(preview);
      onComplete?.(data.url, data);
    } catch (e) {
      setError(e.response?.data?.detail?.message || "Speichern fehlgeschlagen.");
      setPhase("ready");
    }
  };

  const cancel = async () => {
    abortRef.current = true;
    if (staging?.staging_id) {
      try { await api.delete(`/anonymize/staging/${staging.staging_id}`); } catch { /* ignore */ }
    }
    if (preview) URL.revokeObjectURL(preview);
    onCancel?.();
  };

  const busy = phase === "anonymizing" || phase === "confirming" || phase === "uploading"
    || phase === "scanning" || phase === "blocked";
  // Abbrechen/Verwerfen bleibt auch während der (u.U. langen) KI-Verarbeitung möglich.
  const cancelDisabled = phase === "confirming" || phase === "uploading";
  // Bei geblocktem Original (Staging abgelehnt, z.B. expliziter Inhalt in MP/STX/Profil)
  // gibt es kein staging → alle Bedienelemente sperren, nur die Fehlermeldung zeigen.
  const noStaging = !staging || phase === "blocked";

  const painting = brushMode ? "blur" : null;
  const activeStrokes = brushStrokes;
  const setActiveStrokes = setBrushStrokes;
  const activeSize = brushSize;
  const setActiveSize = setBrushSize;

  return (
    <div data-testid="anonymize-dialog" className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center overflow-y-auto">
      <div className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <Shield size={14} /> {t("anonymize.title")}
          </p>
          <button data-testid="anonymize-close" onClick={cancel} className="p-1.5 -mr-1.5" disabled={cancelDisabled}>
            <X size={18} />
          </button>
        </div>

        <div className="bg-[#F5F5F5] flex items-center justify-center relative" style={{ minHeight: "40vh" }}>
          {brushMode && preview ? (
            <BrushCanvas imageUrl={preview} strokes={brushStrokes} onChange={setBrushStrokes} radiusPct={brushSize} />
          ) : preview ? (
            <img src={preview} data-testid="anonymize-preview-img" alt="" className="max-w-full object-contain" style={{ maxHeight: "40vh" }} />
          ) : error ? (
            <ImageOff data-testid="anonymize-preview-error-icon" size={30} className="text-[#BBB]" />
          ) : <Loader2 className="animate-spin text-[#666]" />}
          {(phase === "uploading" || phase === "anonymizing" || phase === "scanning") && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <div className="bg-white border border-[#E5E5E5] px-4 py-3 flex flex-col items-center gap-2 w-56">
                <div className="flex items-center gap-2">
                  {phase === "uploading" ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} className="animate-pulse" />}
                  <span className="text-xs">{phase === "anonymizing" ? t("anonymize.working") : phase === "scanning" ? t("anonymize.scanning") : t("anonymize.uploading")}</span>
                </div>
                {phase === "scanning" && (
                  <p className="text-[10px] text-[#999] text-center leading-snug">{t("anonymize.scanning_hint")}</p>
                )}
                {phase === "anonymizing" && (
                  <>
                    <p className="text-[10px] text-[#999] text-center leading-snug">{t("anonymize.working_hint")}</p>
                    <p data-testid="anonymize-elapsed" className="text-[11px] text-[#0A0A0A] font-medium">{elapsed}s</p>
                  </>
                )}
                {phase === "uploading" && (
                  <div data-testid="anonymize-upload-progress" className="w-full">
                    <div className="h-1.5 w-full bg-[#E5E5E5] rounded-full overflow-hidden">
                      <div className="h-full bg-[#0A0A0A] transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                    </div>
                    <p className="text-[10px] text-[#999] text-center mt-1">{uploadPct}%</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {painting && (
          <div data-testid="brush-toolbar" className="px-4 py-2 border-b bg-amber-50 border-amber-200 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] flex-1 min-w-[120px] text-amber-800">
              {t("anonymize.brush_hint")}
            </span>
            <div className="flex items-center gap-1" data-testid="brush-size">
              {[["XS", 0.02], ["S", 0.035], ["M", 0.06], ["L", 0.09]].map(([lbl, val]) => (
                <button
                  key={lbl}
                  data-testid={`brush-size-${lbl}`}
                  onClick={() => setActiveSize(val)}
                  disabled={busy}
                  className={`h-8 w-8 rounded-full border text-[11px] disabled:opacity-40 ${activeSize === val ? "bg-amber-500 text-white border-amber-500" : "border-amber-400 text-amber-800"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <button
              data-testid="brush-undo-btn"
              onClick={() => setActiveStrokes((s) => s.slice(0, -1))}
              disabled={busy || !activeStrokes.length}
              className="h-8 px-2 rounded-full border border-amber-400 text-amber-800 text-xs flex items-center gap-1 disabled:opacity-40">
              <Undo2 size={12} /> {t("anonymize.undo")}
            </button>
            <button
              data-testid="brush-clear-btn"
              onClick={() => setActiveStrokes([])}
              disabled={busy || !activeStrokes.length}
              className="h-8 px-2 rounded-full border border-amber-400 text-amber-800 text-xs flex items-center gap-1 disabled:opacity-40">
              <Trash2 size={12} /> {t("anonymize.clear")}
            </button>
          </div>
        )}

        <div className="p-4 space-y-2">
          <p className="text-[11px] text-[#666]">{t("anonymize.hint")}</p>

          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("anonymize.face")}</p>
            <label data-testid="anonymize-face-toggle" className="flex items-center justify-between py-1 cursor-pointer">
              <div>
                <span className="text-sm">{t("anonymize.anonymize_face")}</span>
                <p className="text-[10px] text-[#999] mt-0.5">{t("anonymize.face_desc")}</p>
              </div>
              <SwitchToggle
                checked={privacy.pixelate_face}
                disabled={busy || noStaging}
                onChange={() => setPrivacy((c) => ({ ...c, pixelate_face: !c.pixelate_face, blur_face: false }))}
                variant="neutral"
                ariaLabel={t("anonymize.anonymize_face")}
              />
            </label>
          </div>

          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">{t("anonymize.body_markers")}</p>
            {/* Tattoo-Entfernung: vollautomatisch. bekleidet → Gemini, nackt → RunPod/Qwen. */}
            <label data-testid="anonymize-toggle-remove_tattoos" className="flex items-center justify-between py-1.5 cursor-pointer">
              <div className="pr-3">
                <span className="text-sm">{t("anonymize.remove_tattoos")}</span>
                <p className="text-[10px] text-[#999] mt-0.5 leading-snug">{t("anonymize.remove_tattoos_hint")}</p>
              </div>
              <SwitchToggle
                checked={!!privacy.remove_tattoos}
                disabled={busy || noStaging}
                onChange={() => {
                  setError("");
                  setPrivacy((c) => ({ ...c, remove_tattoos: !c.remove_tattoos }));
                }}
                variant="neutral"
                ariaLabel={t("anonymize.remove_tattoos")}
              />
            </label>
            {/* iter282 · Blur-Pinsel: verwischt beliebige Bereiche (kein Entfernen) */}
            <label data-testid="anonymize-toggle-brush" className="flex items-center justify-between py-1.5 cursor-pointer border-t border-[#EEE] mt-1 pt-2">
              <div>
                <span className="text-sm">{t("anonymize.brush_label")}</span>
                <p className="text-[10px] text-[#999] mt-0.5">{t("anonymize.brush_desc")}</p>
              </div>
              <SwitchToggle
                checked={brushMode}
                disabled={busy || noStaging}
                onChange={() => {
                  setBrushMode((v) => !v);
                  setError("");
                }}
                variant="neutral"
                ariaLabel={t("anonymize.brush_label")}
              />
            </label>
          </div>

          {quota && quota.gated && !quota.unlimited && (
            <p data-testid="anonymize-quota-info" className="text-[10px] text-[#999] text-center">
              {t("anonymize.quota_remaining", { remaining: previewBlocked ? 0 : (quota.remaining ?? 0) })}
            </p>
          )}

          {previewBlocked && (
            <div data-testid="anonymize-booking" className="border border-purple-300 bg-purple-50 p-3 space-y-2">
              <p className="text-xs font-semibold text-purple-900">{t("anonymize.quota_exhausted_title")}</p>
              <p className="text-[11px] text-purple-800">{t("anonymize.quota_exhausted_desc")}</p>
              <button
                data-testid="anonymize-book-btn"
                onClick={() => navigate("/studio/helper")}
                className="h-9 px-3 bg-purple-600 text-white text-xs font-medium hover:bg-purple-500 rounded-full">
                {t("anonymize.book_now")}
              </button>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              data-testid="anonymize-preview-btn"
              onClick={createPreview}
              disabled={busy || noStaging || previewBlocked || (brushMode && brushStrokes.length === 0)}
              className="flex-1 h-12 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
              {phase === "anonymizing"
                ? <><Loader2 size={13} className="animate-spin" /> {t("anonymize.working")}</>
                : previewBlocked
                  ? <><Lock size={13} /> {t("anonymize.create_preview")}</>
                  : brushMode
                  ? <><RefreshCw size={13} /> {t("anonymize.create_preview")} ({brushStrokes.length})</>
                  : <><RefreshCw size={13} /> {t("anonymize.create_preview")}</>}
            </button>
            <button
              data-testid="anonymize-apply-btn"
              onClick={applyUpload}
              disabled={busy || noStaging || phase === "uploading"}
              className="flex-1 h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-40">
              {phase === "confirming"
                ? <><Loader2 size={13} className="animate-spin" /> {t("anonymize.saving")}</>
                : <><CheckCircle size={13} /> {t("anonymize.apply")}</>}
            </button>
          </div>

          {!isAnonymized && !previewBlocked && (
            <p className="text-[10px] text-[#999] text-center">{t("anonymize.apply_asis_note")}</p>
          )}

          {error && <div data-testid="anonymize-error" className="border border-red-400 bg-red-50 text-red-700 text-xs p-2">{error}</div>}
          {warn && <div data-testid="anonymize-warn" className="border border-amber-300 bg-amber-50 text-amber-800 text-xs p-2">{warn}</div>}

          <button data-testid="anonymize-cancel-btn" onClick={cancel} disabled={cancelDisabled} className="w-full h-10 rounded-full text-[#666] text-xs disabled:opacity-40">
            {t("anonymize.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnonymizeDialog;
